import axios from 'axios'
import {useEffect, useState} from 'react'
import {API_URL} from '../../../../../../_metronic/helpers'
import {InvoiceSubject} from './invoiceSubject'

export const INVOICE_RULES_V79_PATH = '/invoices/rules/v79'

export type InvoiceRuleStatus =
  | 'Required'
  | 'ConditionallyRequired'
  | 'Optional'
  | 'OutOfPattern'
  | 'ReadOnly'
  | 'Calculated'
  | 'InheritedFromReference'

export type InvoiceFieldRuleV79 = {
  jsonName: string
  titleFa: string
  section: 'Header' | 'Body' | 'Payment'
  dataType?: string
  maxLength?: number
  integerDigits?: number
  decimalDigits?: number
  status: InvoiceRuleStatus
  visible: boolean
  enabled: boolean
  shouldSerialize: boolean
  formula?: string
  errorMessage?: string
  hint?: string
  ruleVersion: string
}

export type InvoiceRowPolicyV79 = {
  minimumRows: number
  maximumRows?: number
  allowEmptyBody: boolean
  fixedRowCount?: number
  allowAdd: boolean
  allowDelete: boolean
  allowNewItemIdentifier: boolean
  readOnlyBody: boolean
  inheritedFromReference: boolean
  quantityConstraint?: string
  duplicateIdentifierPolicy: string
  sourceRule: string
  bodyAllowed: boolean
  bodyEditable: boolean
  newItemIdsAllowed: boolean
  amountMustEqualOne: boolean
  message?: string
}

export type InvoiceRulesV79 = {
  enabled: boolean
  strictValidation: boolean
  instructionVersion: string
  schemaVerificationStatus: 'PdfVerified'
  sourceDocument: string
  sourceSha256: string
  rowPolicy: InvoiceRowPolicyV79
  allowedSubjects: InvoiceSubject[]
  errors: string[]
  fields: InvoiceFieldRuleV79[]
}

type InvoiceFieldRuleV79Wire = Partial<InvoiceFieldRuleV79> & {
  JsonName?: string
  TitleFa?: string
  Section?: InvoiceFieldRuleV79['section']
  DataType?: string
  MaxLength?: number
  IntegerDigits?: number
  DecimalDigits?: number
  Status?: InvoiceRuleStatus
  Visible?: boolean
  Enabled?: boolean
  ShouldSerialize?: boolean
  Formula?: string
  ErrorMessage?: string
  Hint?: string
  RuleVersion?: string
}

type InvoiceRowPolicyV79Wire = Partial<InvoiceRowPolicyV79> & {
  MinimumRows?: number
  MaximumRows?: number
  AllowEmptyBody?: boolean
  FixedRowCount?: number
  AllowAdd?: boolean
  AllowDelete?: boolean
  AllowNewItemIdentifier?: boolean
  ReadOnlyBody?: boolean
  InheritedFromReference?: boolean
  QuantityConstraint?: string
  DuplicateIdentifierPolicy?: string
  SourceRule?: string
  BodyAllowed?: boolean
  BodyEditable?: boolean
  NewItemIdsAllowed?: boolean
  AmountMustEqualOne?: boolean
  Message?: string
}

export type InvoiceRulesV79Wire = Omit<InvoiceRulesV79, 'rowPolicy' | 'fields'> & {
  rowPolicy: InvoiceRowPolicyV79Wire
  fields: InvoiceFieldRuleV79Wire[]
}

const requiredWireValue = <T>(camelCase: T | undefined, pascalCase: T | undefined, name: string): T => {
  const value = camelCase ?? pascalCase
  if (value === undefined) throw new Error(`V7.9 rule response is missing ${name}`)
  return value
}

export const normalizeInvoiceRulesV79 = (wire: InvoiceRulesV79Wire): InvoiceRulesV79 => ({
  ...wire,
  rowPolicy: {
    minimumRows: requiredWireValue(wire.rowPolicy.minimumRows, wire.rowPolicy.MinimumRows, 'minimumRows'),
    maximumRows: wire.rowPolicy.maximumRows ?? wire.rowPolicy.MaximumRows,
    allowEmptyBody: requiredWireValue(wire.rowPolicy.allowEmptyBody, wire.rowPolicy.AllowEmptyBody, 'allowEmptyBody'),
    fixedRowCount: wire.rowPolicy.fixedRowCount ?? wire.rowPolicy.FixedRowCount,
    allowAdd: requiredWireValue(wire.rowPolicy.allowAdd, wire.rowPolicy.AllowAdd, 'allowAdd'),
    allowDelete: requiredWireValue(wire.rowPolicy.allowDelete, wire.rowPolicy.AllowDelete, 'allowDelete'),
    allowNewItemIdentifier: requiredWireValue(
      wire.rowPolicy.allowNewItemIdentifier,
      wire.rowPolicy.AllowNewItemIdentifier,
      'allowNewItemIdentifier'
    ),
    readOnlyBody: requiredWireValue(wire.rowPolicy.readOnlyBody, wire.rowPolicy.ReadOnlyBody, 'readOnlyBody'),
    inheritedFromReference: requiredWireValue(
      wire.rowPolicy.inheritedFromReference,
      wire.rowPolicy.InheritedFromReference,
      'inheritedFromReference'
    ),
    quantityConstraint: wire.rowPolicy.quantityConstraint ?? wire.rowPolicy.QuantityConstraint,
    duplicateIdentifierPolicy: requiredWireValue(
      wire.rowPolicy.duplicateIdentifierPolicy,
      wire.rowPolicy.DuplicateIdentifierPolicy,
      'duplicateIdentifierPolicy'
    ),
    sourceRule: requiredWireValue(wire.rowPolicy.sourceRule, wire.rowPolicy.SourceRule, 'sourceRule'),
    bodyAllowed: requiredWireValue(wire.rowPolicy.bodyAllowed, wire.rowPolicy.BodyAllowed, 'bodyAllowed'),
    bodyEditable: requiredWireValue(wire.rowPolicy.bodyEditable, wire.rowPolicy.BodyEditable, 'bodyEditable'),
    newItemIdsAllowed: requiredWireValue(
      wire.rowPolicy.newItemIdsAllowed,
      wire.rowPolicy.NewItemIdsAllowed,
      'newItemIdsAllowed'
    ),
    amountMustEqualOne: requiredWireValue(
      wire.rowPolicy.amountMustEqualOne,
      wire.rowPolicy.AmountMustEqualOne,
      'amountMustEqualOne'
    ),
    message: wire.rowPolicy.message ?? wire.rowPolicy.Message,
  },
  fields: wire.fields.map((field) => ({
    jsonName: requiredWireValue(field.jsonName, field.JsonName, 'jsonName'),
    titleFa: requiredWireValue(field.titleFa, field.TitleFa, 'titleFa'),
    section: requiredWireValue(field.section, field.Section, 'section'),
    dataType: field.dataType ?? field.DataType,
    maxLength: field.maxLength ?? field.MaxLength,
    integerDigits: field.integerDigits ?? field.IntegerDigits,
    decimalDigits: field.decimalDigits ?? field.DecimalDigits,
    status: requiredWireValue(field.status, field.Status, 'status'),
    visible: requiredWireValue(field.visible, field.Visible, 'visible'),
    enabled: requiredWireValue(field.enabled, field.Enabled, 'enabled'),
    shouldSerialize: requiredWireValue(
      field.shouldSerialize,
      field.ShouldSerialize,
      'shouldSerialize'
    ),
    formula: field.formula ?? field.Formula,
    errorMessage: field.errorMessage ?? field.ErrorMessage,
    hint: field.hint ?? field.Hint,
    ruleVersion: requiredWireValue(field.ruleVersion, field.RuleVersion, 'ruleVersion'),
  })),
})

export type InvoiceFieldValueMap = Record<string, unknown>

export const getInvoiceFieldRule = (
  rules: InvoiceRulesV79 | undefined,
  jsonName: string
): InvoiceFieldRuleV79 | undefined => rules?.fields.find((field) => field.jsonName === jsonName)

export const isInvoiceFieldVisible = (
  rules: InvoiceRulesV79 | undefined,
  jsonName: string,
  legacyFallback = true
) => (rules?.enabled ? getInvoiceFieldRule(rules, jsonName)?.visible === true : legacyFallback)

export const isInvoiceFieldRequired = (
  rules: InvoiceRulesV79 | undefined,
  jsonName: string,
  legacyFallback = false
) => {
  if (!rules?.enabled) return legacyFallback
  return getInvoiceFieldRule(rules, jsonName)?.status === 'Required'
}

export const isInvoiceFieldReadOnly = (
  rules: InvoiceRulesV79 | undefined,
  jsonName: string,
  legacyFallback = false
) => {
  if (!rules?.enabled) return legacyFallback
  const rule = getInvoiceFieldRule(rules, jsonName)
  return (
    rule?.enabled === false ||
    rule?.status === 'ReadOnly' ||
    rule?.status === 'Calculated' ||
    rule?.status === 'InheritedFromReference'
  )
}

export type InvoiceUnitRuleV79 = {
  status: InvoiceRuleStatus
  visible: boolean
  required: boolean
  readOnly: boolean
}

export const resolveInvoiceUnitRule = (
  rules: InvoiceRulesV79 | undefined,
  invoiceType: number,
  invoicePattern: number,
  invoiceSubject: InvoiceSubject
): InvoiceUnitRuleV79 => {
  const resolvedRule = rules?.enabled ? getInvoiceFieldRule(rules, 'mu') : undefined
  // Keep the item form deterministic while the canonical rule request is loading or unavailable.
  // This fallback mirrors the V7.9 Table 1 status for mu; an enabled canonical rule always wins.
  const status: InvoiceRuleStatus = resolvedRule
    ? resolvedRule.status
    : invoiceSubject === 3
    ? 'InheritedFromReference'
    : invoiceType === 1 && invoicePattern === 11
    ? 'Required'
    : invoiceType === 1 && [2, 8].includes(invoicePattern)
    ? 'OutOfPattern'
    : 'Optional'

  return {
    status,
    visible: resolvedRule ? resolvedRule.visible : status !== 'OutOfPattern',
    required: status === 'Required',
    readOnly:
      resolvedRule?.enabled === false ||
      status === 'ReadOnly' ||
      status === 'Calculated' ||
      status === 'InheritedFromReference',
  }
}

export const isServerOnlyInvoiceField = (jsonName: string) =>
  jsonName === 'insr' || jsonName === 'indati2m'

export const hasOrganizationUnitCode = (unit: {label?: unknown} | undefined) => {
  if (!unit || typeof unit.label !== 'string') return false
  const label = unit.label.trim()
  return label.length > 0 && !label.startsWith('*')
}

export const omitOutOfPatternInvoiceFields = <T extends InvoiceFieldValueMap>(
  values: T,
  rules: InvoiceRulesV79 | undefined
): Partial<T> => {
  if (!rules?.enabled) return {...values}
  const result = {...values}
  rules.fields
    .filter((field) => !field.shouldSerialize || field.status === 'OutOfPattern')
    .forEach((field) => delete result[field.jsonName])
  return result
}

export const getOutOfPatternFieldsWithValues = (
  values: InvoiceFieldValueMap,
  rules: InvoiceRulesV79 | undefined
) => {
  if (!rules?.enabled) return []
  return rules.fields.filter((field) => {
    if (field.shouldSerialize && field.status !== 'OutOfPattern') return false
    const value = values[field.jsonName]
    return value !== undefined && value !== null && value !== ''
  })
}

export const canAddInvoiceRow = (policy: InvoiceRowPolicyV79 | undefined, rowCount: number) => {
  if (!policy) return true
  return (
    policy.allowAdd &&
    !policy.readOnlyBody &&
    policy.allowNewItemIdentifier &&
    (!policy.maximumRows || rowCount < policy.maximumRows)
  )
}

type RuleContext = {
  invoiceType: number
  invoicePattern: number
  invoiceSubject: InvoiceSubject
  isArticle9: boolean
  settlementMethod?: number
  buyerType?: number
}

export const useInvoiceRulesV79 = (context: RuleContext) => {
  const [rules, setRules] = useState<InvoiceRulesV79>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const {invoiceType, invoicePattern, invoiceSubject, isArticle9, settlementMethod, buyerType} =
    context

  useEffect(() => {
    let active = true
    setRules(undefined)
    setLoading(true)
    setError('')
    axios
      .get<InvoiceRulesV79Wire>(`${API_URL}${INVOICE_RULES_V79_PATH}`, {
        params: {invoiceType, invoicePattern, invoiceSubject, isArticle9, settlementMethod, buyerType},
      })
      .then(({data}) => {
        if (active) setRules(normalizeInvoiceRulesV79(data))
      })
      .catch(() => {
        if (active) setError('دریافت قوانین نسخه V7.9 ناموفق بود؛ ثبت نهایی همچنان در سرور کنترل می‌شود.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [
    invoiceType,
    invoicePattern,
    invoiceSubject,
    isArticle9,
    settlementMethod,
    buyerType,
  ])

  return {rules, loading, error}
}
